import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const DecisionsPanel = ({ limit = 10 }) => {
  const [actionsData, setActionsData] = useState({ en_cours: [], en_retard: [] });
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('en_retard'); // en_retard, en_cours, toutes

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 120000); // Refresh toutes les 2 minutes
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [actions, statsData] = await Promise.all([
        api.collaboration.pv.actionsEnCours(),
        api.collaboration.pv.statistiques()
      ]);

      setActionsData(actions);
      setStats(statsData);
    } catch (error) {
      console.error('Erreur chargement actions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTerminer = async (pvId, actionId) => {
    if (!window.confirm('Marquer cette action comme terminée ?')) return;
    
    try {
      await api.collaboration.pv.terminerAction(pvId, actionId);
      loadData();
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  const getPrioriteColor = (priorite) => {
    const colors = {
      haute: '#f44336',
      normale: '#4caf50',
      basse: '#9e9e9e'
    };
    return colors[priorite] || '#666';
  };

  const getStatutColor = (statut) => {
    const colors = {
      a_faire: '#ff9800',
      en_cours: '#2196f3',
      terminee: '#4caf50',
      annulee: '#9e9e9e'
    };
    return colors[statut] || '#666';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      a_faire: 'À faire',
      en_cours: 'En cours',
      terminee: 'Terminée',
      annulee: 'Annulée'
    };
    return labels[statut] || statut;
  };

  const isEnRetard = (echeance) => {
    return new Date(echeance) < new Date();
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const diffDays = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `⚠️ Retard de ${Math.abs(diffDays)}j`;
    if (diffDays === 0) return "🔥 Aujourd'hui";
    if (diffDays === 1) return "⚡ Demain";
    if (diffDays <= 7) return `📅 Dans ${diffDays}j`;
    return d.toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
        ⏳ Chargement des actions...
      </div>
    );
  }

  const getDisplayActions = () => {
    if (activeTab === 'en_retard') return actionsData.en_retard || [];
    if (activeTab === 'en_cours') return actionsData.en_cours || [];
    return [...(actionsData.en_retard || []), ...(actionsData.en_cours || [])];
  };

  const displayActions = getDisplayActions().slice(0, limit);

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* En-tête avec stats */}
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
          📋 Décisions & Actions de suivi
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 10,
          marginBottom: 16
        }}>
          <div style={{ 
            background: '#e3f2fd', 
            padding: 12, 
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1976d2' }}>
              {stats.total_decisions || 0}
            </div>
            <div style={{ fontSize: 11, color: '#666' }}>Décisions</div>
          </div>
          <div style={{ 
            background: '#fff3e0', 
            padding: 12, 
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#f57c00' }}>
              {stats.actions_en_cours || 0}
            </div>
            <div style={{ fontSize: 11, color: '#666' }}>En cours</div>
          </div>
          <div style={{ 
            background: '#ffebee', 
            padding: 12, 
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#c62828' }}>
              {actionsData.total_en_retard || 0}
            </div>
            <div style={{ fontSize: 11, color: '#666' }}>En retard</div>
          </div>
          <div style={{ 
            background: '#e8f5e9', 
            padding: 12, 
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#388e3c' }}>
              {stats.taux_realisation || 0}%
            </div>
            <div style={{ fontSize: 11, color: '#666' }}>Réalisé</div>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '2px solid #f0f0f0' }}>
        <button
          onClick={() => setActiveTab('en_retard')}
          style={{
            padding: '8px 16px',
            fontSize: 12,
            fontWeight: 600,
            border: 'none',
            background: 'none',
            color: activeTab === 'en_retard' ? '#f44336' : '#999',
            borderBottom: activeTab === 'en_retard' ? '2px solid #f44336' : 'none',
            marginBottom: -2,
            cursor: 'pointer'
          }}
        >
          ⚠️ En retard ({actionsData.total_en_retard || 0})
        </button>
        <button
          onClick={() => setActiveTab('en_cours')}
          style={{
            padding: '8px 16px',
            fontSize: 12,
            fontWeight: 600,
            border: 'none',
            background: 'none',
            color: activeTab === 'en_cours' ? '#2196f3' : '#999',
            borderBottom: activeTab === 'en_cours' ? '2px solid #2196f3' : 'none',
            marginBottom: -2,
            cursor: 'pointer'
          }}
        >
          🔄 En cours ({actionsData.total_en_cours || 0})
        </button>
        <button
          onClick={() => setActiveTab('toutes')}
          style={{
            padding: '8px 16px',
            fontSize: 12,
            fontWeight: 600,
            border: 'none',
            background: 'none',
            color: activeTab === 'toutes' ? '#666' : '#999',
            borderBottom: activeTab === 'toutes' ? '2px solid #666' : 'none',
            marginBottom: -2,
            cursor: 'pointer'
          }}
        >
          📋 Toutes
        </button>
      </div>

      {/* Liste des actions */}
      {displayActions.length === 0 ? (
        <div style={{ 
          padding: 30, 
          textAlign: 'center', 
          background: '#f9f9f9', 
          borderRadius: 10,
          color: '#66bb6a'
        }}>
          ✅ Aucune action dans cette catégorie
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {displayActions.map(action => {
            const enRetard = isEnRetard(action.echeance);
            return (
              <div
                key={action.id}
                style={{
                  background: enRetard ? '#ffebee' : 'white',
                  border: `1px solid ${enRetard ? '#ef535020' : '#e0e0e0'}`,
                  borderLeft: `4px solid ${getPrioriteColor(action.priorite)}`,
                  borderRadius: 8,
                  padding: 12
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 600, 
                      fontSize: 13,
                      marginBottom: 4,
                      color: '#333'
                    }}>
                      {action.intitule}
                    </div>
                    
                    {action.procesVerbal && (
                      <div style={{ 
                        fontSize: 11, 
                        color: '#999',
                        marginBottom: 6
                      }}>
                        📄 {action.procesVerbal.titre}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* Responsable */}
                      {action.responsable && (
                        <span style={{ 
                          fontSize: 11,
                          color: '#666',
                          background: '#f5f5f5',
                          padding: '2px 8px',
                          borderRadius: 4
                        }}>
                          👤 {action.responsable.prenom} {action.responsable.nom}
                        </span>
                      )}
                      
                      {/* Échéance */}
                      <span style={{ 
                        fontSize: 11,
                        color: enRetard ? '#c62828' : '#666',
                        fontWeight: enRetard ? 600 : 400
                      }}>
                        {formatDate(action.echeance)}
                      </span>

                      {/* Priorité */}
                      <span style={{
                        fontSize: 10,
                        color: getPrioriteColor(action.priorite),
                        background: getPrioriteColor(action.priorite) + '20',
                        padding: '2px 6px',
                        borderRadius: 3,
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {action.priorite}
                      </span>

                      {/* Statut */}
                      <span style={{
                        fontSize: 10,
                        color: getStatutColor(action.statut),
                        background: getStatutColor(action.statut) + '20',
                        padding: '2px 6px',
                        borderRadius: 3
                      }}>
                        {getStatutLabel(action.statut)}
                      </span>
                    </div>
                  </div>

                  {/* Bouton terminer */}
                  {action.statut !== 'terminee' && (
                    <button
                      onClick={() => handleTerminer(action.pv_id, action.id)}
                      style={{
                        padding: '6px 12px',
                        fontSize: 11,
                        border: 'none',
                        borderRadius: 6,
                        background: '#4caf50',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                      title="Marquer comme terminée"
                    >
                      ✓ Terminer
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DecisionsPanel;
